/**
 * Preview Side Panel Component
 *
 * Inline side panel for previewing workspace items (tables, linked tables).
 * Renders as part of the page layout without blocking interaction with
 * the main content.
 *
 * Features:
 * - Table preview: Schema, versions, API links
 * - Linked table preview: Configuration, API links
 * - Status management
 * - Favorite toggle
 * - Tag management
 * - Drag & drop file upload for tables (visual feedback)
 *
 * Part of the Data Workspace OS feature set.
 *
 * @module features/workspace/components/PreviewPanel/PreviewSidePanel
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Upload } from 'lucide-react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { Tabs, TabsList, TabsTrigger, TabsContent, ScrollArea } from '@loader/ui';
import type {
  TableApiResponse,
  LinkedTableApiResponse,
  TableStatus,
  LinkedTableStatus,
} from '@loader/shared-types';

import { useResolvedConnectorType } from '../../hooks/useResolvedConnectorType';
import type { PreviewItem } from './types';
import { PreviewPanelHeader } from './PreviewPanelHeader';
import { PreviewPanelStats } from './PreviewPanelStats';
import { SchemaTab } from './SchemaTab';
import { LinkedTableConfigTab } from './LinkedTableConfigTab';
import { VersionsTab } from './VersionsTab';
import { ApiTab } from './ApiTab';

import { toast } from '@/lib/toast';
import { useToggleFavoriteMutation } from '@/features/organization/hooks';
import { useTableVersionsQuery } from '@/features/tables/hooks/useTableQueries';
import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';
import { validateFile } from '@/lib/fileUtils';
import { createApiError } from '@/lib/apiErrors';
import { useWorkspaceMembership } from '@/hooks';

export interface PreviewSidePanelProps {
  item: PreviewItem | null;
  onClose: () => void;
}

export function PreviewSidePanel({
  item,
  onClose,
}: PreviewSidePanelProps) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  // Fetch workspace membership to determine canEdit
  const { canEdit } = useWorkspaceMembership();

  // Toggle favorite mutation
  const toggleFavoriteMutation = useToggleFavoriteMutation();

  const handleToggleFavorite = useCallback(async () => {
    if (!item) return;

    // Handle optimistic update based on item type
    if (item.type === 'table') {
      const queryKey = queryKeys.tables.detail(item.id);
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<TableApiResponse>(queryKey);

      queryClient.setQueryData<TableApiResponse>(queryKey, (old) => {
        if (!old) return old;
        return { ...old, is_favorite: !old.is_favorite };
      });

      toggleFavoriteMutation.mutate(
        { itemType: item.type, itemId: item.id },
        {
          onError: (err) => {
            queryClient.setQueryData(queryKey, previousData);
            toast.workspace.favoriteFailed(err instanceof Error ? err.message : 'Unknown error');
          },
        }
      );
    } else if (item.type === 'linked_table') {
      const queryKey = queryKeys.linkedTables.detail(item.id);
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<LinkedTableApiResponse>(queryKey);

      queryClient.setQueryData<LinkedTableApiResponse>(queryKey, (old) => {
        if (!old) return old;
        return { ...old, is_favorite: !old.is_favorite };
      });

      toggleFavoriteMutation.mutate(
        { itemType: item.type, itemId: item.id },
        {
          onError: (err) => {
            queryClient.setQueryData(queryKey, previousData);
            toast.workspace.favoriteFailed(err instanceof Error ? err.message : 'Unknown error');
          },
        }
      );
    }
  }, [item, toggleFavoriteMutation, queryClient]);

  // ============================================================================
  // DRAG & DROP STATE
  // ============================================================================

  /** Track when a file is being dragged over the panel */
  const [isDragOver, setIsDragOver] = useState(false);
  /** Counter to handle nested drag events */
  const dragCounterRef = useRef(0);
  /** Staged file from drag-and-drop (passed to VersionsTab) */
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  /** Track active tab to switch to versions when file is dropped */
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Reset state when item changes
  useEffect(() => {
    setDroppedFile(null);
    setActiveTab(null);
  }, [item?.id]);

  // Fetch table details
  const { data: tableDetails, isLoading: isLoadingTable, refetch: refetchTable } = useQuery({
    queryKey: item?.id ? queryKeys.tables.detail(item.id) : ['disabled'],
    queryFn: async () => {
      if (!item?.id) throw new Error('No item ID');
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.tables[':id'].$get({
        param: { id: item.id },
      });
      if (!res.ok) throw await createApiError(res, 'table');
      const data = (await res.json()) as { table: TableApiResponse };
      return data.table;
    },
    enabled: !!item?.id && item?.type === 'table',
  });

  // Fetch linked table details
  const { data: linkedTableDetails, isLoading: isLoadingLinkedTable, refetch: refetchLinkedTable } = useQuery({
    queryKey: item?.id ? queryKeys.linkedTables.detail(item.id) : ['disabled'],
    queryFn: async () => {
      if (!item?.id) throw new Error('No item ID');
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private['linked-tables'][':id'].$get({
        param: { id: item.id },
      });
      if (!res.ok) throw await createApiError(res, 'linked table');
      const data = (await res.json()) as { linkedTable: LinkedTableApiResponse };
      return data.linkedTable;
    },
    enabled: !!item?.id && item?.type === 'linked_table',
  });

  // Fetch versions for tables
  const { data: versions = [] } = useTableVersionsQuery(
    item?.type === 'table' ? item?.id : undefined,
    { enabled: item?.type === 'table' && !!item?.id }
  );

  // Status mutations - invalidate TanStack Query caches only
  const updateStatusMutation = useMutation({
    mutationFn: async ({ tableId, status }: { tableId: string; status: TableStatus }) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.tables[':id'].$put({
        param: { id: tableId },
        json: { status },
      });
      if (!res.ok) throw new Error('Failed to update status');
      return res.json() as Promise<{ table: TableApiResponse }>;
    },
    onSuccess: () => {
      void refetchTable();
      void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
      void queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'dataGrid' &&
          query.queryKey[1] === 'tables',
      });
      toast.table.statusUpdated();
    },
    onError: () => toast.table.statusUpdateFailed(),
  });

  const updateLinkedTableStatusMutation = useMutation({
    mutationFn: async ({
      linkedTableId,
      status,
    }: {
      linkedTableId: string;
      status: LinkedTableStatus;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private['linked-tables'][':id'].$put({
        param: { id: linkedTableId },
        json: { status },
      });
      if (!res.ok) throw new Error('Failed to update status');
      return res.json() as Promise<{ linkedTable: LinkedTableApiResponse }>;
    },
    onSuccess: () => {
      void refetchLinkedTable();
      void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
      void queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'dataGrid' &&
          query.queryKey[1] === 'linked-tables',
      });
      toast.table.statusUpdated();
    },
    onError: () => toast.table.statusUpdateFailed(),
  });

  const handleStatusChange = (newStatus: string) => {
    if (!item?.id) return;
    if (item.type === 'table') {
      updateStatusMutation.mutate({ tableId: item.id, status: newStatus as TableStatus });
    } else if (item.type === 'linked_table') {
      updateLinkedTableStatusMutation.mutate({
        linkedTableId: item.id,
        status: newStatus as LinkedTableStatus,
      });
    }
  };

  // ============================================================================
  // DRAG & DROP HANDLERS (for tables only)
  // ============================================================================

  /**
   * Handle drag enter event
   * Uses counter to handle nested elements
   */
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only allow drag for tables
      if (item?.type !== 'table') return;

      dragCounterRef.current++;
      if (dragCounterRef.current === 1) {
        setIsDragOver(true);
      }
    },
    [item?.type]
  );

  /**
   * Handle drag leave event
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  /**
   * Handle drag over event (required for drop to work)
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only allow drag for tables
      if (item?.type !== 'table') return;
    },
    [item?.type]
  );

  /**
   * Handle file drop - stages file for upload confirmation
   * File is passed to VersionsTab where user can confirm the upload
   */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Reset drag state
      dragCounterRef.current = 0;
      setIsDragOver(false);

      // Only allow drop for tables
      if (!item?.id || item.type !== 'table') return;

      const file = e.dataTransfer.files?.[0];
      if (!file) return;

      // Validate file before staging
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.version.validationError(validation.errors[0] || 'Invalid file');
        return;
      }

      // Stage the file and switch to versions tab
      setDroppedFile(file);
      setActiveTab('versions');
    },
    [item?.id, item?.type]
  );

  /**
   * Clear the dropped file when upload is complete or cancelled
   */
  const handleClearDroppedFile = useCallback(() => {
    setDroppedFile(null);
  }, []);

  // Memoize versions array to prevent unnecessary re-renders in ApiTab
  // Filter for completed versions only - version pickers should not show failed versions
  // Must be before early return to satisfy rules-of-hooks
  const memoizedVersions = useMemo(() =>
    versions
      .filter((v) => v.status === 'completed')
      .map((v) => ({
        id: v.id,
        version_number: v.version_number,
        file_name: v.file_name,
        created_at: v.created_at,
        system_tag: v.system_tag,
        tags: v.tags,
      })),
    [versions]
  );

  // Memoize linkedTableSources to prevent useEffect re-runs in ApiTab
  // Must be before early return to satisfy rules-of-hooks
  const memoizedLinkedTableSources = useMemo(() =>
    linkedTableDetails?.tables?.map((t: { id: string; alias: string; target_id: string; type: 'table' | 'linked_table' }) => ({
      id: t.id,
      alias: t.alias,
      target_id: t.target_id,
      type: t.type,
    })),
    [linkedTableDetails?.tables]
  );

  // Resolve connector type from tables list cache (detail endpoint doesn't include it)
  const displayConnectorType = useResolvedConnectorType(
    item?.id ?? '',
    item?.type ?? '',
    item?.connectorType
  );

  if (!item) return null;

  // Determine loading state based on item type
  const isStatsLoading = item.type === 'table' ? isLoadingTable : isLoadingLinkedTable;

  // Use fetched data for name/description (Zustand item might have empty values from URL restore)
  const displayName = item.type === 'table'
    ? (tableDetails?.name ?? item.name)
    : (linkedTableDetails?.name ?? item.name);
  const displayDescription = item.type === 'table'
    ? (tableDetails?.description ?? item.description)
    : (linkedTableDetails?.description ?? item.description);
  const displayIsFavorite = item.type === 'table'
    ? (tableDetails?.is_favorite ?? item.isFavorite)
    : (linkedTableDetails?.is_favorite ?? item.isFavorite);

  const columnCount = item.type === 'table' ? tableDetails?.fields?.length : undefined;
  const versionCount = versions.length;
  const sourceTablesCount = linkedTableDetails?.tables?.length || 0;
  const filtersCount = linkedTableDetails?.filters?.length || 0;
  const joinsCount = linkedTableDetails?.joins?.length || 0;
  const calculatedColumnsCount = linkedTableDetails?.calculated_columns?.length || 0;

  // Determine if drop overlay should be visible
  const showDropOverlay = isDragOver && item.type === 'table';

  return (
    <div
      className="relative flex h-full flex-col border-l bg-background"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop overlay - shown when dragging file over panel (tables only) */}
      {showDropOverlay && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary rounded-lg">
          <div className="flex flex-col items-center gap-3 text-primary">
            <Upload className="size-10" />
            <div className="text-center">
              <p className="text-sm font-medium">Drop to select file</p>
              <p className="text-xs text-primary/70">.csv, .json, .xlsx, .parquet</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <PreviewPanelHeader
        name={displayName}
        type={item.type}
        description={displayDescription}
        connectorType={displayConnectorType}
        isFavorite={displayIsFavorite}
        onToggleFavorite={handleToggleFavorite}
        onClose={onClose}
      />

      {/* Stats Section */}
      <PreviewPanelStats
        type={item.type}
        columnCount={columnCount}
        versionCount={versionCount}
        sourceTablesCount={sourceTablesCount}
        joinsCount={joinsCount}
        filtersCount={filtersCount}
        calculatedColumnsCount={calculatedColumnsCount}
        status={item.type === 'table' ? tableDetails?.status : linkedTableDetails?.status}
        onStatusChange={handleStatusChange}
        isUpdatingStatus={
          updateStatusMutation.isPending || updateLinkedTableStatusMutation.isPending
        }
        isLoading={isStatsLoading}
        canEdit={canEdit}
      />

      {/* Tabs */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t px-4 pt-4">
        <Tabs
          key={item.id}
          value={activeTab ?? (item.type === 'linked_table' ? 'config' : 'versions')}
          onValueChange={setActiveTab}
          className="flex min-h-0 flex-1 flex-col"
        >
          <TabsList className="w-full shrink-0">
            {item.type === 'table' && (
              <TabsTrigger value="versions" className="flex-1">
                Versions
              </TabsTrigger>
            )}
            {item.type === 'linked_table' && (
              <TabsTrigger value="config" className="flex-1">
                Config
              </TabsTrigger>
            )}
            <TabsTrigger value="api" className="flex-1">
              Links
            </TabsTrigger>
            {item.type === 'table' && (
              <TabsTrigger value="schema" className="flex-1">
                Schema
              </TabsTrigger>
            )}
          </TabsList>

          <ScrollArea className="min-h-0 flex-1 overflow-auto">
            {item.type === 'table' && (
              <TabsContent value="versions">
                <VersionsTab
                  tableId={item.id}
                  connectorType={displayConnectorType}
                  droppedFile={droppedFile}
                  onClearDroppedFile={handleClearDroppedFile}
                  canEdit={canEdit}
                />
              </TabsContent>
            )}

            {item.type === 'linked_table' && (
              <TabsContent value="config">
                <LinkedTableConfigTab
                  linkedTableDetails={linkedTableDetails}
                  isLoading={isLoadingLinkedTable}
                />
              </TabsContent>
            )}

            <TabsContent value="api">
              <ApiTab
                itemId={item.id}
                itemType={item.type}
                versions={memoizedVersions}
                linkedTableSources={memoizedLinkedTableSources}
                variables={linkedTableDetails?.variables ?? undefined}
              />
            </TabsContent>

            {item.type === 'table' && (
              <TabsContent value="schema">
                <SchemaTab tableDetails={tableDetails} isLoading={isLoadingTable} />
              </TabsContent>
            )}
          </ScrollArea>
        </Tabs>
      </div>
    </div>
  );
}
